import type { ReactNode } from "react";
import { css } from "../../styled-system/css";
import { token } from "../../styled-system/tokens";
import logo from "../assets/logo.png";

type LandingTo = "/login/company" | "/login/store";

type RenderLinkProps = {
  children: ReactNode;
  className: string;
  to: LandingTo;
};

export type ConsoleLandingCard = {
  ctaTone?: "accent" | "default";
  description: string;
  icon?: ReactNode;
  showArrow?: boolean;
  title: string;
  to: LandingTo;
};

type ConsoleLandingPageProps = {
  arrowIcon: ReactNode;
  cards: ConsoleLandingCard[];
  renderLink: (props: RenderLinkProps) => ReactNode;
};

const pageStyle = css({
  minH: "100vh",
  display: "flex",
  flexDirection: "column",
  bg: "surface.primary",
});

const headerStyle = css({
  h: "52px",
  px: "20px",
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid",
  borderColor: "border.secondary",
  bg: "surface.primary",
  flexShrink: 0,
});

const headerBrandStyle = css({
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const mainStyle = css({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  bg: "background.background",
});

const titleRowStyle = css({
  display: "flex",
  alignItems: "center",
  gap: "8",
  h: "74px",
  w: "full",
  borderLeft: "1px solid",
  borderColor: "border.secondary",
  px: "40px",
  py: "24px",
});

const cardViewportStyle = css({
  display: "flex",
  justifyContent: "center",
  w: "full",
  px: { base: "4", md: "0" },
  pb: "10",
});

const cardRowStyle = css({
  display: "flex",
  flexDirection: { base: "column", md: "row" },
  gap: { base: "4", md: "8" },
  justifyContent: "center",
  w: "full",
  maxW: "800px",
});

const cardLinkStyle = css({
  display: "block",
  flex: { base: "none", md: "1 0 0" },
  w: "full",
  maxW: { base: "full", md: "384px" },
  minW: 0,
  textDecoration: "none",
  color: "inherit",
  _focusVisible: {
    outlineStyle: "solid",
    outlineWidth: "2px",
    outlineColor: "border.accentPrimary",
    outlineOffset: "2px",
    borderRadius: "16px",
  },
});

const cardStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "4",
  h: "200px",
  px: "4",
  py: "1",
  bg: "surface.primary",
  border: "1px solid",
  borderColor: "border.secondary",
  borderRadius: "16px",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
  textAlign: "center",
});

const titleGroupStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1",
});

const ctaStyle = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minH: "32px",
});

const ctaDefaultContentStyle = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "1",
  p: "2",
  borderRadius: "8px",
  color: "text.primary",
});

const ctaTextStyle = css({
  fontSize: "12px",
  fontWeight: "600",
  lineHeight: "1.35",
});

const ctaArrowGlyphStyle = css({
  width: "9.75px",
  height: "9.59px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const loggedInBadgeStyle = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0",
  px: "8px",
  py: "2px",
  borderRadius: "full",
  background: "surface.accentPrimary",
  color: "text.inverse",
});

const loggedInBadgeTextStyle = css({
  fontSize: "12px",
  fontWeight: "600",
  lineHeight: "1.35",
  color: "text.inverse",
  px: "2px",
});

export function ConsoleLandingPage({
  arrowIcon,
  cards,
  renderLink,
}: ConsoleLandingPageProps) {
  return (
    <div data-panda-theme="v2" className={pageStyle}>
      <header className={headerStyle}>
        <div className={headerBrandStyle}>
          <img
            src={logo}
            alt="ポケットサイン"
            style={{ height: "14.5px", width: "110.8px" }}
          />
          <span
            style={{
              color: token.var("colors.text.secondary"),
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            地域ポイント
          </span>
        </div>
      </header>

      <main className={mainStyle}>
        <div className={titleRowStyle}>
          <h1
            style={{
              margin: 0,
              color: token.var("colors.text.primary"),
              fontFamily: "'Noto Sans JP', sans-serif",
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            管理画面選択
          </h1>
        </div>

        <div className={cardViewportStyle}>
          <div className={cardRowStyle}>
            {cards.map((card) => (
              <LandingCard
                key={card.to}
                arrowIcon={arrowIcon}
                ctaTone={card.ctaTone}
                description={card.description}
                icon={card.icon}
                renderLink={renderLink}
                showArrow={card.showArrow}
                title={card.title}
                to={card.to}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

type LandingCardProps = {
  arrowIcon: ReactNode;
  ctaTone?: "accent" | "default";
  description: string;
  icon?: ReactNode;
  renderLink: (props: RenderLinkProps) => ReactNode;
  showArrow?: boolean;
  title: string;
  to: LandingTo;
};

function LandingCard({
  arrowIcon,
  ctaTone = "default",
  description,
  icon,
  renderLink,
  showArrow = true,
  title,
  to,
}: LandingCardProps) {
  return renderLink({
    className: cardLinkStyle,
    to,
    children: (
      <div className={cardStyle}>
        {icon ? (
          <div
            aria-hidden="true"
            style={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </div>
        ) : null}

        <div className={titleGroupStyle}>
          <div
            style={{
              color: token.var("colors.text.primary"),
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            {title}
          </div>

          <div className={ctaStyle}>
            {ctaTone === "accent" ? (
              <div
                className={loggedInBadgeStyle}
                style={{
                  backgroundColor: token.var("colors.surface.accentPrimary"),
                  borderRadius: "9999px",
                  color: token.var("colors.text.inverse"),
                  ["--fill-0" as string]: token.var("colors.text.inverse"),
                }}
              >
                <span
                  className={loggedInBadgeTextStyle}
                  style={{
                    color: token.var("colors.text.inverse"),
                    fontWeight: 600,
                  }}
                >
                  {description}
                </span>
                {showArrow ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className={ctaArrowGlyphStyle}>{arrowIcon}</span>
                  </span>
                ) : null}
              </div>
            ) : (
              <div className={ctaDefaultContentStyle}>
                <span className={ctaTextStyle}>{description}</span>
                {showArrow ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className={ctaArrowGlyphStyle}>{arrowIcon}</span>
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
  });
}
